/**
 * DebugPanel — collapsible QR20 channel state display.
 * Toggle via Ctrl+D keyboard shortcut.
 */

import { useState, useEffect, type ReactNode } from 'react';
import { shortenAddress } from '@tokenstreaming/shared';
import type { StreamingEngine, EngineState } from '../modules/streaming/types.js';
import type { ConnectionState, SignalingState } from '../modules/webrtc/types.js';

interface Props {
  engine: StreamingEngine | null;
  connectionState: ConnectionState;
  signalingState: SignalingState;
  channelId: string;
}

export function DebugPanel({ engine, connectionState, signalingState, channelId }: Props): ReactNode {
  const [visible, setVisible] = useState(false);
  const [channelData, setChannelData] = useState<{
    sequence: number;
    creatorAmount: string;
    resolverAmount: string;
    intentDigest: string;
    engineState: EngineState;
  } | null>(null);

  // Keyboard shortcut: Ctrl+D
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.key === 'd') {
        e.preventDefault();
        setVisible(v => !v);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  // Update channel data
  useEffect(() => {
    if (!engine) return;
    const interval = setInterval(() => {
      const state = engine.getChannelState();
      if (state) {
        setChannelData({
          sequence: state.sequence,
          creatorAmount: state.creatorAmount.toString(),
          resolverAmount: state.resolverAmount.toString(),
          intentDigest: state.intentDigest,
          engineState: engine.getState(),
        });
      }
    }, 500);
    return () => clearInterval(interval);
  }, [engine]);

  if (!visible) return null;

  return (
    <div className="debug-panel">
      <div className="debug-header">
        <span>Debug Panel</span>
        <button className="btn-ghost btn-xs" onClick={() => setVisible(false)}>Close</button>
      </div>
      <div className="debug-body">
        <div className="debug-row">
          <span className="debug-label">Channel ID</span>
          <span className="debug-value mono">{shortenAddress(channelId, 8)}</span>
        </div>
        <div className="debug-row">
          <span className="debug-label">Signaling</span>
          <span className={`debug-value badge-${signalingState === 'joined' ? 'success' : 'warning'}`}>
            {signalingState}
          </span>
        </div>
        <div className="debug-row">
          <span className="debug-label">WebRTC</span>
          <span className={`debug-value badge-${connectionState === 'connected' ? 'success' : 'warning'}`}>
            {connectionState}
          </span>
        </div>
        {channelData && (
          <>
            <div className="debug-row">
              <span className="debug-label">Engine State</span>
              <span className="debug-value">{channelData.engineState}</span>
            </div>
            <div className="debug-row">
              <span className="debug-label">Sequence</span>
              <span className="debug-value mono">{channelData.sequence}</span>
            </div>
            <div className="debug-row">
              <span className="debug-label">Creator Amt</span>
              <span className="debug-value mono">{channelData.creatorAmount}</span>
            </div>
            <div className="debug-row">
              <span className="debug-label">Resolver Amt</span>
              <span className="debug-value mono">{channelData.resolverAmount}</span>
            </div>
            <div className="debug-row">
              <span className="debug-label">Intent Digest</span>
              <span className="debug-value mono">{channelData.intentDigest.slice(0, 18)}...</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
